import React, { useEffect, useRef, useState } from 'react';
import { getLocalTime } from '../../utilities/time';
import { Text } from '../typography';
import styles from './ActionItemComment.module.scss';
import { useCurrentUser } from '../../providers/UserProvider';
import { loader } from 'graphql.macro';
import { useMutation } from '@apollo/client';
import Icon from '../Icon';
import { Form } from 'react-bootstrap';
import { useModal } from '../../providers/ModalProvider';
import { keys } from '../../utilities/translator/translation_keys';
import { useTranslation } from 'react-i18next';

const updateCommentMutation = loader('./ActionItemComment.update.graphql');
const deleteCommentMutation = loader('./ActionItemComment.delete.graphql');

export default function ActionItemComment({ comment, refetch = () => {} }) {
  const { t } = useTranslation();
  const { openConfirmationModal } = useModal();
  const { user } = useCurrentUser();
  const [isEditing, setIsEditing] = useState(false);
  const commentRef = useRef(null);

  const [updateComment] = useMutation(updateCommentMutation);
  const [deleteComment] = useMutation(deleteCommentMutation);
  const isCreator = comment?.creator?.id === user?.id;
  const wasEdited = comment?.dateCreated !== comment?.dateModified;

  const handleOutsideClick = (e) => {
    if (commentRef.current && !commentRef.current.contains(e.target)) {
      setTimeout(() => {
        setIsEditing(false);
      }, 100);
    }
  };

  useEffect(() => {
    if (isEditing) document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isEditing]);

  return (
    <div className={styles.container}>
      <img
        className={styles.avatar}
        src={comment.creator.avatarUrl}
        alt={`${comment.creator.firstName} ${comment.creator.lastName}s avatar`}
      />
      <div className={styles.rightContainer}>
        <div className={styles.header}>
          <div className={styles.flexRow}>
            <Text
              size="md"
              weight="bold"
              noMargin
            >{`${comment.creator.firstName} ${comment.creator.lastName}`}</Text>
            <Text size="sm" noMargin color="secondary">
              {`${getLocalTime(comment.dateCreated).format(
                'dddd MMMM Do YYYY HH:mm',
              )} ${
                wasEdited ? `(${t(keys.common.EDITED).toLowerCase()})` : ''
              }`}
            </Text>
          </div>
          {isCreator && (
            <div>
              <Icon
                className={styles.icon}
                style={{ fontSize: '1rem' }}
                color={isEditing ? 'green' : null}
                onClick={() => setIsEditing(!isEditing)}
              >
                {isEditing ? 'check' : 'edit'}
              </Icon>
              <Icon
                className={styles.icon}
                style={{ fontSize: '1rem' }}
                color="red"
                onClick={() =>
                  openConfirmationModal({
                    variant: 'warning',
                    title: t(keys.action.DELETE_VARIABLE, {
                      variable: t(keys.common.THIS_COMMENT).toLowerCase(),
                    }),
                    description: t(keys.action.DELETE_CONFIRMATION, {
                      variable: t(keys.common.THIS_COMMENT).toLowerCase(),
                    }),
                    onSubmit: () =>
                      deleteComment({
                        variables: { actionItemCommentId: comment?.id },
                      }).then(() => refetch()),
                  })
                }
              >
                delete
              </Icon>
            </div>
          )}
        </div>
        {isEditing ? (
          <Form.Control
            as="textarea"
            rows={2}
            ref={commentRef}
            defaultValue={comment.text}
            onBlur={(e) =>
              updateComment({
                variables: {
                  actionItemCommentId: comment?.id,
                  text: e.target.value,
                },
              })
            }
          />
        ) : (
          <Text size="md" prewrap noMargin>
            {comment.text}
          </Text>
        )}
      </div>
    </div>
  );
}
